// api/form2307API.js
//
// Read / download / reprint for Form 2307 certificates under
// /client/tax-obligation.
//
// There is deliberately NO create call here. A 2307 is produced automatically
// when a VAT transaction carrying an ATC is posted, so the VAT screens never
// ask for one to be generated — they only display and download what posting
// already produced. Hand-keying a correction still goes through
// formAPI.createForm2307Records, which requires an explicit reason.
import axiosInstance from './axiosConfig';

/**
 * Read the filename the server chose from Content-Disposition, so a saved
 * certificate matches the record rather than a name the browser invented.
 * Mirrors features/chart-of-accounts/api/exportCoa.js.
 */
function filenameFromResponse(response, fallback) {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return match ? match[1].trim() : fallback;
}

/**
 * The server sends errors as JSON, but responseType:'blob' means even the error
 * body arrives as a Blob — unwrap it so callers get a real message instead of
 * "[object Blob]".
 */
async function messageFromBlobError(error, fallback) {
  const data = error.response?.data;
  if (data instanceof Blob && data.type.includes('json')) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.message || error.message || fallback;
}

/** Hand the browser a blob as a download, and return the filename used. */
function saveBlob(response, fallbackName) {
  const filename = filenameFromResponse(response, fallbackName);
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return filename;
}

const form2307API = {
  /**
   * List the 2307s already generated for a client.
   *
   * @param {string} clientId
   * @param {object} [params] { quarter, month, fiscalYear, vendorId, includeVoided }
   */
  list: async (clientId, params = {}) => {
    const response = await axiosInstance.get(`/client/tax-obligation/${clientId}/2307`, { params });
    return response.data;
  },

  /** One certificate as JSON. */
  getById: async (clientId, certificateId) => {
    const response = await axiosInstance.get(`/client/tax-obligation/${clientId}/2307/${certificateId}`);
    return response.data;
  },

  /** Download or reprint a single certificate. */
  downloadPdf: async (clientId, certificateId, fallbackName = 'form-2307.pdf') => {
    try {
      const response = await axiosInstance.get(
        `/client/tax-obligation/${clientId}/2307/${certificateId}/pdf`,
        { responseType: 'blob' }
      );
      return saveBlob(response, fallbackName);
    } catch (error) {
      throw new Error(await messageFromBlobError(error, 'Failed to download Form 2307'));
    }
  },

  /**
   * Download the quarterly batch for one counterparty: every certificate for
   * that payor/payee in the quarter, on a single Form 2307. This is the sheet
   * BIR expects per quarter per counterparty, and it is why a government
   * counterparty's EWT and VAT-withholding lines appear on one form.
   */
  downloadQuarterlyPdf: async (clientId, vendorId, { quarter, fiscalYear, includeVoided } = {}) => {
    try {
      const response = await axiosInstance.get(
        `/client/tax-obligation/${clientId}/2307/quarterly/${vendorId}/pdf`,
        {
          params: { quarter, fiscalYear, ...(includeVoided ? { includeVoided: true } : {}) },
          responseType: 'blob',
        }
      );
      return saveBlob(response, `form-2307-${fiscalYear}-Q${quarter}.pdf`);
    } catch (error) {
      throw new Error(await messageFromBlobError(error, 'Failed to download the quarterly Form 2307'));
    }
  },
};

export default form2307API;
